import React, { useCallback, useEffect, useState } from 'react';
import {
  accountState,
  authenticationState,
  currentRmaState,
  loadingState,
  lookupState,
} from 'state/atoms';
import { useNotifyError } from 'utils/hooks';
import { useLazyQuery } from '@apollo/client';
import { CHECK_WARRANTY_STATUS } from 'connectors/queries/warranty';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { FormWrapper, SubmitButton, SearchInput, SearchError } from './styledComponents';
import SearchIcon from '@material-ui/icons/Search';
import { MAXIMUM_NO_OF_ITEMS_ON_CASE, MAXIMUM_NO_OF_ITEMS_ON_CASE_MESSAGE } from 'utils/constants';

const SerialNumberLookup = () => {
  const setLoading = useSetRecoilState(loadingState);
  const currentRma = useRecoilValue(currentRmaState);
  const [currentLookupState, setLookupResults] = useRecoilState(lookupState);
  const { endCustomerAccountId } = useRecoilValue(authenticationState);
  const { defaultCurrencyCode } = useRecoilValue(accountState);
  const notifyError = useNotifyError({
    fallbackMessage:
      'Something went wrong while searching for this serial number. Please contact the RMA team at RMA@calix.com if this problem persists.',
  });

  const [checkWarrantyStatus, { loading = false, refetch }] = useLazyQuery(CHECK_WARRANTY_STATUS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const results = data.getAccount.checkWarrantyStatus;
      setLookupResults({
        ...currentLookupState,
        lookupResults: results,
        selectedResult: results.length === 1 ? results[0] : null,
      });
    },
    onError: notifyError,
  });

  const [term, setTerm] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const onSubmit = useCallback(
    (event) => {
      event.preventDefault();

      if (!term) {
        setErrorMessage('Type a Serial Number to search');
        return;
      }

      const isSerialAlreadyAdded = currentRma.items.some((item) => item.serialNumber === term);

      if (isSerialAlreadyAdded) {
        setErrorMessage(
          `The serial number ${term} is already in the RMA case. Click View Case Details for more information.`
        );
        return;
      }

      setErrorMessage('');

      const variables = {
        accountId: endCustomerAccountId,
        input: { serialNumbers: [term], currencyCode: defaultCurrencyCode },
        images: {
          primaryOnly: true,
          sizes: ['Small'],
        },
      };

      if (refetch) {
        refetch(variables);
      } else {
        checkWarrantyStatus({ variables });
      }
    },
    [endCustomerAccountId, currentRma, term, checkWarrantyStatus, refetch, defaultCurrencyCode]
  );

  useEffect(() => {
    setLoading(loading);
  }, [loading, setLoading]);

  useEffect(() => {
    if (currentRma.items.length >= MAXIMUM_NO_OF_ITEMS_ON_CASE) {
      setErrorMessage(MAXIMUM_NO_OF_ITEMS_ON_CASE_MESSAGE);
    }
  }, [currentRma.items]);

  return (
    <>
      <FormWrapper onSubmit={onSubmit}>
        <SearchInput
          type="text"
          placeholder="Serial Number"
          value={term}
          autoFocus
          disabled={loading || currentRma.items.length >= MAXIMUM_NO_OF_ITEMS_ON_CASE}
          onChange={(event) => setTerm(event.currentTarget.value.trim())}
        />
        <SubmitButton type="submit" disabled={loading}>
          <SearchIcon />
        </SubmitButton>
        <SearchError>{errorMessage}</SearchError>
      </FormWrapper>
    </>
  );
};

export default SerialNumberLookup;
